import { Spine, SpineTexture, TextureAtlas, type SkeletonData } from "@esotericsoftware/spine-pixi-v8";
import { Container, Rectangle, RendererType, Texture, TextureSource, type Renderer, type WebGLRenderer } from "pixi.js";
import type { Log } from "../log/Log";
import { createSkeletonData } from "../spine/createSkeletonData";
import { installClipCache, tessellateRiveClips } from "./riveClipping";
import { installGroupAlpha } from "./riveGroupAlpha";
import { decodeRiveAtlasPages, installRiveTextureSampling, setRiveSpineResolution } from "./riveTextureSampling";

/** Where one converted skeleton's files are served (rive-spine's output for one Rive file). */
export interface SpineArtUrls {
  /** The skeleton (Spine 4.2 JSON). */
  skeleton: string;
  /** Its atlas: a page per image. */
  atlas: string;
  /** Every page of the atlas in one file (rive-spine's pages bundle). */
  pages: string;
}

/** The calls that move a skeleton as its Rive file's inputs moved its state machine, by the input's name. */
export interface SpineInputs {
  numbers?: Readonly<Record<string, (value: number) => void>>;
  booleans?: Readonly<Record<string, (value: boolean) => void>>;
  triggers?: Readonly<Record<string, () => void>>;
}

/** What the controls move: a skeleton and its animation state (a `Spine`'s, or spine-core's alone in a test). */
export type SpinePose = Pick<Spine, "skeleton" | "state">;

/** One artboard of a converted file: its size and where the skeleton's origin lies in it, and its controls. */
export interface SpineArtboardSpec {
  /** The Rive artboard's size, in its pixels: the skeleton's units. */
  artboard: { readonly width: number; readonly height: number };
  /** The skeleton's origin, in the artboard's pixels (rive-spine's manifest: the point the artist anchors it by). */
  origin: { readonly x: number; readonly y: number };
  /**
   * Poses a new skeleton as its Rive state machine starts (its colour, its loops) and returns its inputs: the names its
   * view sets, as the Rive file's view model had them.
   */
  controls(spine: SpinePose): SpineInputs;
  /** Engine seconds between two drawn frames at least (`advance`); 1/30 when absent. */
  frameSeconds?: number;
  /** Whether its texture is multisampled (antialiased edges, as Rive's canvas); true when absent. */
  antialias?: boolean;
  /**
   * Its skeleton's URL when it is not the files' own: another artboard of the same Rive file, converted over the same
   * atlas and pages (slot-coin-hold's edge fire: its top and bottom pieces), which then load and decode once for both.
   */
  skeleton?: string;
}

/** What a load takes: the renderer it draws with, the sink it logs to. */
export interface SpineArtboardDeps {
  renderer: Renderer;
  log: Log;
  /** Fetches a file (the browser's `fetch` when absent; a test passes its own). */
  fetch?: (url: string) => Promise<Response>;
}

/** A loaded atlas and how many artboards drawn from it are still in use: its pages' textures go with the last. */
interface SharedAtlas {
  atlas: TextureAtlas;
  users: number;
}

const DEFAULT_FRAME_SECONDS = 1 / 30;
/** A drawn frame is due a little early rather than a display frame late (two 60 Hz frames sum just under 1/30 s). */
const FRAME_TOLERANCE_SECONDS = 0.002;
/** Texture pixels per artboard pixel until the title sizes the texture (`resize`). */
const INITIAL_SCALE = 0.5;
/** Clears the texture to nothing before each drawn frame. */
const TRANSPARENT: [number, number, number, number] = [0, 0, 0, 0];

/** A texture over part of the artboard, and that part in artboard pixels. */
interface ArtboardView {
  texture: Texture;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * One artboard of an artist's Rive file as rive-spine converted it (a Spine 4.2 skeleton whose images are the file's,
 * drawn as Rive draws them), drawn into a texture of its own (decision 72): the surface `RiveArtboard` (decision 66)
 * gives, so a title's views draw either the same way — a sprite, a part of it on its own sprite, a comet bent along a
 * rope (slot-coin-hold's, the first title that played it). `advance` adds a
 * frame's seconds and draws when a drawn frame is due (`frameSeconds`, the cap), `render` draws at once: the skeleton
 * advances on the caller's engine time and is drawn by Pixi's renderer into a multisampled texture (antialiased edges, as
 * Rive's canvas has them; screen-blended layers blend over the artboard's own, as in Rive), on the GPU — no canvas
 * copied, nothing uploaded. A clip mask with curves (the converter's `rive.clips`) is cut as Rive cuts it at the scale
 * the artboard is drawn at, whenever that changes, and every clip's convex parts are kept while it holds still (decision
 * 75). The texture is the artboard at `resize`'s scale; every texture here is its part of the
 * artboard at its size in artboard pixels whatever the texture's, so a resize changes only how sharp they are. The Rive
 * file's inputs are calls (`SpineArtboardSpec.controls`), set by the view model's names.
 */
export class SpineArtboard {
  /** The whole artboard. */
  public readonly texture: Texture;
  /** The artboard's own size, in its pixels: the units of every texture here, and the texture at scale 1. */
  public readonly size: { readonly width: number; readonly height: number };
  private readonly source: TextureSource;
  private readonly views: ArtboardView[] = [];
  /** What the renderer draws: the skeleton at its origin. */
  private readonly stage = new Container({ label: "SpineArtboard" });
  /** Input names the controls do not have: logged once each. */
  private readonly unknown = new Set<string>();
  /** Engine seconds since the last drawn frame. */
  private pendingSeconds = 0;
  private disposed = false;

  private constructor(
    private readonly spine: Spine,
    private readonly inputs: SpineInputs,
    private readonly spec: SpineArtboardSpec,
    private readonly shared: SharedAtlas,
    private readonly deps: SpineArtboardDeps,
  ) {
    this.size = { width: spec.artboard.width, height: spec.artboard.height };
    this.source = new TextureSource({ width: this.size.width, height: this.size.height, resolution: INITIAL_SCALE, antialias: spec.antialias ?? true, label: "SpineArtboard" });
    this.stage.addChild(spine);
    this.texture = this.view(0, 0, this.size.width, this.size.height);
    this.resize(INITIAL_SCALE);
  }

  /**
   * Several artboards of one converted file (its files at `urls`, each loaded once — a spec's own `skeleton` too, over
   * the same atlas): one per spec, in its order, each a skeleton of its own posed by its controls; every one null,
   * logged, when a file fails. Nothing is drawn yet: set the inputs, then `render(0)` before the texture shows. The
   * pages' textures go with the last of them.
   */
  public static async loadAll(urls: SpineArtUrls, specs: readonly SpineArtboardSpec[], deps: SpineArtboardDeps): Promise<(SpineArtboard | null)[]> {
    const loaded = await loadSkeletons(urls, [...new Set(specs.map((spec) => spec.skeleton ?? urls.skeleton))], deps);
    if (!loaded) return specs.map(() => null);
    // Every clip's convex parts kept while it holds still: spine-core's own, so the same pixels at a fraction of the cost.
    installClipCache();
    const shared = loaded.atlas;
    const artboards = specs.map((spec) => {
      try {
        const url = spec.skeleton ?? urls.skeleton;
        const spine = new Spine({ skeletonData: loaded.data.get(url)!, autoUpdate: false });
        // Rive's group opacities, where the converter keyed them on control slots (slot-coin-hold's smoke and streak).
        installGroupAlpha(spine.state, spine.skeleton);
        const inputs = spec.controls(spine);
        shared.users += 1;
        const artboard = new SpineArtboard(spine, inputs, spec, shared, deps);
        // Its curved clip masks cut as Rive cuts them, at its texture's pixels per artboard pixel (slot-coin-hold's coin).
        tessellateRiveClips(spine, loaded.json.get(url), () => artboard.scale);
        return artboard;
      } catch (error) {
        deps.log(`[SpineArtboard] "${spec.skeleton ?? urls.skeleton}" not posed: ${error instanceof Error ? error.message : String(error)}`);
        return null;
      }
    });
    if (shared.users === 0) shared.atlas.dispose();
    return artboards;
  }

  /**
   * Draws at `scale` texture pixels per artboard pixel from the next drawn frame on — the artboard's size on the screen
   * in device pixels, never above its own (1: its images' pixels) and never under one pixel.
   */
  public resize(scale: number): void {
    if (this.disposed || !(scale > 0)) return;
    const least = 1 / Math.max(1, Math.min(this.size.width, this.size.height));
    this.source.resize(this.size.width, this.size.height, Math.min(1, Math.max(least, scale)));
    // The texture is whole pixels, so the source's size is the artboard's give or take a fraction of one: each view's
    // frame is refitted to keep its part of the artboard exactly, and the skeleton is drawn to fill the source.
    for (const view of this.views) this.fit(view);
    const kx = this.source.width / this.size.width;
    const ky = this.source.height / this.size.height;
    this.spine.scale.set(kx, ky);
    this.spine.position.set(this.spec.origin.x * kx, this.spec.origin.y * ky);
  }

  /** The texture's scale now (texture pixels per artboard pixel). */
  public get scale(): number {
    return this.source.resolution;
  }

  /**
   * A part of the artboard as a texture of its own, in artboard pixels: one artboard drawn once, its parts on separate
   * sprites (each can move on its own). It follows every resize; it goes with the artboard.
   */
  public region(x: number, y: number, width: number, height: number): Texture {
    return this.view(x, y, width, height);
  }

  /** Sets a number input (an unknown name is logged once and ignored). */
  public setNumber(name: string, value: number): void {
    this.input(name, this.inputs.numbers?.[name])?.(value);
  }

  /** Sets a boolean input (an unknown name is logged once and ignored). */
  public setBoolean(name: string, value: boolean): void {
    this.input(name, this.inputs.booleans?.[name])?.(value);
  }

  /** Fires a trigger input (an unknown name is logged once and ignored). */
  public trigger(name: string): void {
    this.input(name, this.inputs.triggers?.[name])?.();
  }

  /** Adds `deltaSeconds` of engine time; a drawn frame due, advances by all of it and draws (true when it drew). */
  public advance(deltaSeconds: number): boolean {
    if (this.disposed) return false;
    this.pendingSeconds += deltaSeconds;
    if (this.pendingSeconds < (this.spec.frameSeconds ?? DEFAULT_FRAME_SECONDS) - FRAME_TOLERANCE_SECONDS) return false;
    const seconds = this.pendingSeconds;
    this.pendingSeconds = 0;
    this.render(seconds);
    return true;
  }

  /** Advances the skeleton `deltaSeconds` and draws the frame into the texture, now (engine time, never in a render). */
  public render(deltaSeconds: number): void {
    if (this.disposed) return;
    const { renderer } = this.deps;
    this.spine.update(deltaSeconds);
    // Rive's mip level per image is reckoned in the texture's pixels.
    setRiveSpineResolution(this.source.resolution);
    renderer.render({ container: this.stage, target: this.texture, clear: true, clearColor: TRANSPARENT });
    // The multisampled frame resolved into the texture (Pixi does it only for its filters).
    renderer.renderTarget.finishRenderPass();
  }

  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stage.destroy({ children: true });
    for (const view of this.views) if (view.texture !== this.texture) view.texture.destroy(false);
    this.texture.destroy(false);
    this.source.destroy();
    this.shared.users -= 1;
    if (this.shared.users === 0) this.shared.atlas.dispose();
  }

  /** A texture `width` × `height` in size over that part of the artboard (dynamic: its sprites follow a refit). */
  private view(x: number, y: number, width: number, height: number): Texture {
    const texture = new Texture({ source: this.source, frame: new Rectangle(0, 0, 1, 1), orig: new Rectangle(0, 0, width, height), dynamic: true });
    const view = { texture, x, y, width, height };
    this.views.push(view);
    this.fit(view);
    return texture;
  }

  private fit(view: ArtboardView): void {
    const kx = this.source.width / this.size.width;
    const ky = this.source.height / this.size.height;
    view.texture.frame.copyFrom(new Rectangle(view.x * kx, view.y * ky, view.width * kx, view.height * ky));
    view.texture.update();
  }

  private input<T>(name: string, found: T | undefined): T | null {
    if (this.disposed) return null;
    if (found === undefined && !this.unknown.has(name)) {
      this.unknown.add(name);
      this.deps.log(`[SpineArtboard] the skeleton has no input "${name}"`);
    }
    return found ?? null;
  }
}

/**
 * The skeletons at `skeletons` over the atlas at `urls`, its pages decoded as Rive decodes its images, every file fetched
 * at once; null, logged, when a file fails. The first load puts Rive's texture sampling in place for every Spine slot
 * (WebGL 2: elsewhere Pixi's batcher draws them).
 */
async function loadSkeletons(
  urls: SpineArtUrls,
  skeletons: readonly string[],
  deps: SpineArtboardDeps,
): Promise<{ atlas: SharedAtlas; data: Map<string, SkeletonData>; json: Map<string, unknown> } | null> {
  const get = deps.fetch ?? ((url: string) => fetch(url));
  const read = async <T>(url: string, body: (response: Response) => Promise<T>): Promise<T> => {
    const response = await get(url);
    if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
    return body(response);
  };
  try {
    const [jsons, atlasText, pages] = await Promise.all([
      Promise.all(skeletons.map((url) => read(url, (response) => response.json() as Promise<unknown>))),
      read(urls.atlas, (response) => response.text()),
      read(urls.pages, async (response) => new Uint8Array(await response.arrayBuffer())),
    ]);
    if (drawsWithRiveSampling(deps.renderer)) installRiveTextureSampling();
    const atlas = new TextureAtlas(atlasText);
    const { sources, undecoded } = await decodeRiveAtlasPages(atlas.pages.map((page) => page.name), pages, urls.pages);
    for (const page of atlas.pages) page.setTexture(SpineTexture.from(sources[page.name]!));
    // A page the browser would not decode ahead is decoded as it uploads: now, not in the frame that first draws it.
    if (undecoded.length > 0) {
      deps.log(`[SpineArtboard] ${urls.pages}: ${undecoded.length} of ${atlas.pages.length} pages not decoded ahead (${undecoded[0]!.reason}): decoded as they upload, now`);
      for (const { name } of undecoded) deps.renderer.texture.initSource(sources[name]!);
    }
    const data = new Map(skeletons.map((url, i) => [url, createSkeletonData({ skeleton: jsons[i], atlas })] as const));
    // Each skeleton's JSON as read: its curved clips (`rive.clips`) are cut per artboard, at the scale it is drawn at.
    const json = new Map(skeletons.map((url, i) => [url, jsons[i]] as const));
    return { atlas: { atlas, users: 0 }, data, json };
  } catch (error) {
    deps.log(`[SpineArtboard] "${skeletons.join('", "')}" not loaded: ${error instanceof Error ? error.message : String(error)}`);
    return null;
  }
}

/** Whether the renderer draws Spine slots with Rive's texture sampling (its shader is GLSL ES 3: WebGL 2 only). */
function drawsWithRiveSampling(renderer: Renderer): boolean {
  return renderer.type === RendererType.WEBGL && (renderer as WebGLRenderer).context.webGLVersion === 2;
}
